import React, { useCallback, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardTitle,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Flex,
  FlexItem,
  GridItem,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  NumberInput,
  Popover,
} from '@patternfly/react-core';
import { FileInvoiceDollarIcon, LockIcon } from '@patternfly/react-icons';
import { VirtualMachine } from '../../../types/models/VirtualMachine';
import { ApiResponseVmBilling } from '../../../types/api/ApiResponseVirtualMachine';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useUserContext } from '../../../contexts/user/UserContext';
import { formatBareDateNumeric } from '../../../utils/dateFormat';

interface VmBillingCardProps {
  vm: VirtualMachine;
  onVmUpdate: () => void;
}

const clampDay = (day: number): number => Math.max(1, Math.min(28, day));

/** Today's day-of-month, capped at the backend's anchor ceiling of 28. */
const defaultBillingDay = (): number => Math.min(new Date().getDate(), 28);

/**
 * Staff-only monthly-billing enrollment card for the VM detail Overview tab.
 * Enrollment targets the impersonated tenant by design, so the gate is
 * `is_ancalagon_staff` alone — never combined with `!isImpersonating`.
 */
const VmBillingCard: React.FunctionComponent<VmBillingCardProps> = ({ vm, onVmUpdate }) => {
  const user = useUserContext();
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [isPending, setIsPending] = useState(false);
  const [isUnenrollModalOpen, setIsUnenrollModalOpen] = useState(false);
  const [isEnrollPopoverOpen, setIsEnrollPopoverOpen] = useState(false);
  const [enrollDay, setEnrollDay] = useState<number | ''>(defaultBillingDay);

  const runBillingAction = useCallback(
    async (method: 'PUT' | 'DELETE', day?: number) => {
      if (vm.id === null) {
        return;
      }
      setIsPending(true);
      try {
        const response = await apiFetch(`/datacenters/vms/${vm.id}/billing`, {
          method,
          ...(day !== undefined ? { body: JSON.stringify({ day }) } : {}),
        });

        let body: ApiResponseVmBilling | null;
        try {
          body = (await response.json()) as ApiResponseVmBilling;
        } catch {
          body = null;
        }

        if (!body || body.status !== 'success' || !body.data?.billing) {
          const message =
            body?.message ?? `Request failed with status ${response.status} ${response.statusText}`.trim();
          throw new Error(message);
        }

        addNotification({
          title: method === 'PUT' ? 'VM enrolled for monthly billing' : 'VM unenrolled from billing',
          srTitle: 'Success alert',
          description:
            method === 'PUT'
              ? `${vm.name} is now enrolled for monthly billing — next billing date ${formatBareDateNumeric(
                  body.data.billing.next_billing_at,
                )}.`
              : `${vm.name} is no longer billed monthly. Billing history is kept.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        setIsUnenrollModalOpen(false);
        setIsEnrollPopoverOpen(false);
        onVmUpdate();
      } catch (err) {
        addNotification({
          title: method === 'PUT' ? 'Enrollment failed' : 'Unenrollment failed',
          srTitle: 'Error alert',
          description: err instanceof Error ? err.message : 'An unexpected error occurred.',
          variant: 'danger',
        });
        setDrawerOpen(true);
      } finally {
        setIsPending(false);
      }
    },
    [apiFetch, vm.id, vm.name, addNotification, setDrawerOpen, onVmUpdate],
  );

  const handleEnrollToggle = () => {
    if (!isEnrollPopoverOpen) {
      setEnrollDay(defaultBillingDay());
    }
    setIsEnrollPopoverOpen(!isEnrollPopoverOpen);
  };

  const handleDayChange = (event: React.FormEvent<HTMLInputElement>) => {
    const raw = (event.target as HTMLInputElement).value;
    setEnrollDay(raw === '' ? '' : Number(raw));
  };

  // Functional updates only: PatternFly's NumberInput rewrites an emptied field
  // to '0' and fires onChange before onBlur within the same event, so the render
  // closure here can still hold the stale pre-blur value.
  const handleDayBlur = () => {
    setEnrollDay((cur) => (typeof cur === 'number' && !Number.isNaN(cur) ? clampDay(Math.round(cur)) : cur));
  };

  const handleDayStep = (delta: number) => {
    setEnrollDay((cur) =>
      typeof cur === 'number' && !Number.isNaN(cur) ? clampDay(Math.round(cur) + delta) : defaultBillingDay(),
    );
  };

  const isDayValid = typeof enrollDay === 'number' && Number.isInteger(enrollDay) && enrollDay >= 1 && enrollDay <= 28;

  if (user?.is_ancalagon_staff !== true) {
    return null;
  }

  // The contract guarantees `billing` on Show, but a backend that predates the
  // billing feature won't send it — degrade to no card instead of crashing the
  // whole detail page for staff during the deploy window.
  if (!vm.billing) {
    return null;
  }

  const isReplica = vm.replica_of !== null;
  const { enrolled, next_billing_at } = vm.billing;

  return (
    <GridItem span={12}>
      <Card>
        <CardTitle>
          <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsCenter' }}>
            <FlexItem>Billing</FlexItem>
            <FlexItem>
              <Label variant="outline" isCompact icon={<LockIcon />}>
                Staff
              </Label>
            </FlexItem>
          </Flex>
        </CardTitle>
        <CardBody>
          <DescriptionList isHorizontal isCompact>
            <DescriptionListGroup>
              <DescriptionListTerm>Enrollment</DescriptionListTerm>
              <DescriptionListDescription>
                {enrolled ? <Label color="green">Enrolled</Label> : <Label color="grey">Not enrolled</Label>}
              </DescriptionListDescription>
            </DescriptionListGroup>
            {enrolled && (
              <DescriptionListGroup>
                <DescriptionListTerm>Next billing date</DescriptionListTerm>
                <DescriptionListDescription>{formatBareDateNumeric(next_billing_at)}</DescriptionListDescription>
              </DescriptionListGroup>
            )}
          </DescriptionList>
        </CardBody>
        <CardFooter>
          {enrolled ? (
            // Unenroll stays available even on a replica: the backend keeps DELETE
            // working regardless of live NetBox state, and running billing must
            // always be stoppable from here.
            <Button
              variant="link"
              isInline
              isDanger
              onClick={() => setIsUnenrollModalOpen(true)}
              isDisabled={isPending}
            >
              Unenroll
            </Button>
          ) : isReplica ? (
            <span style={{ color: 'var(--pf-t--global--text--color--subtle)' }}>
              Replicas are not billable — enroll the primary.
            </span>
          ) : (
            <Popover
              isVisible={isEnrollPopoverOpen}
              shouldClose={() => {
                if (!isPending) {
                  setIsEnrollPopoverOpen(false);
                }
              }}
              withFocusTrap
              elementToFocus="#vm-billing-day-input"
              headerContent="Billing day of the month"
              bodyContent={
                <>
                  <p>
                    Day the monthly invoice is anchored to (1–28) — align it with the client&apos;s existing
                    recurring invoices to avoid over- or under-invoicing. A day already past this month anchors
                    next month.
                  </p>
                  <div className="pf-v6-u-mt-sm">
                    <NumberInput
                      value={enrollDay}
                      min={1}
                      max={28}
                      onMinus={() => handleDayStep(-1)}
                      onPlus={() => handleDayStep(1)}
                      onChange={handleDayChange}
                      onBlur={handleDayBlur}
                      inputAriaLabel="Billing day of the month"
                      minusBtnAriaLabel="Decrease billing day"
                      plusBtnAriaLabel="Increase billing day"
                      inputProps={{ id: 'vm-billing-day-input' }}
                      validated={isDayValid ? 'default' : 'error'}
                      widthChars={3}
                      isDisabled={isPending}
                    />
                  </div>
                </>
              }
              footerContent={
                <Flex spaceItems={{ default: 'spaceItemsSm' }}>
                  <FlexItem>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        if (typeof enrollDay === 'number' && isDayValid) {
                          runBillingAction('PUT', enrollDay);
                        }
                      }}
                      isLoading={isPending}
                      isDisabled={isPending || !isDayValid}
                      spinnerAriaLabel="Enrolling VM for monthly billing"
                    >
                      Enroll
                    </Button>
                  </FlexItem>
                  <FlexItem>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => setIsEnrollPopoverOpen(false)}
                      isDisabled={isPending}
                    >
                      Cancel
                    </Button>
                  </FlexItem>
                </Flex>
              }
            >
              <Button
                variant="secondary"
                icon={<FileInvoiceDollarIcon />}
                onClick={handleEnrollToggle}
                isDisabled={isPending}
              >
                Enroll for monthly billing
              </Button>
            </Popover>
          )}
        </CardFooter>
      </Card>

      {isUnenrollModalOpen && (
        <Modal
          variant={ModalVariant.small}
          isOpen={isUnenrollModalOpen}
          onClose={() => {
            if (!isPending) {
              setIsUnenrollModalOpen(false);
            }
          }}
          aria-label="Confirm unenroll from billing"
        >
          <ModalHeader title="Unenroll from billing" titleIconVariant="warning" />
          <ModalBody>
            Stop monthly billing for <strong>{vm.name}</strong>? Billing history is kept; the VM can be re-enrolled
            later — re-enrollment never double-bills an already-covered period.
          </ModalBody>
          <ModalFooter>
            <Button
              variant="danger"
              onClick={() => runBillingAction('DELETE')}
              isLoading={isPending}
              isDisabled={isPending}
              spinnerAriaLabel="Unenrolling VM from billing"
            >
              Unenroll
            </Button>
            <Button variant="link" onClick={() => setIsUnenrollModalOpen(false)} isDisabled={isPending}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </GridItem>
  );
};

export default VmBillingCard;
